// Командная палитра Cmd+K — фирменная механика «Терминала», перенесённая в CRM.
//
// ОТКУДА. Конкурсный прототип дизайн-2026/terminal (решение владельца 01.08.2026):
// палитра — центр управления, навигация одним списком, у каждой команды справа её
// горячая клавиша — пассивное обучение хоткеям (паттерн Superhuman/Raycast).
// Открывается БЕЗ анимации: «Терминал» прямо ссылается на Vercel — анимация на
// сотом открытии за день — это cognitive burden, а не полировка.
//
// СОСТАВ КОМАНД НЕ ЖИВЁТ ЗДЕСЬ. Источник — NAV_GROUPS из nav.ts, тот же, что у
// бокового меню: добавили раздел в меню — он сам появился в палитре. Права
// приходят предикатом `denied` (useRightsApi().denied из Layout): закрытый роли
// раздел в командах НЕ показывается — ссылка на 403 хуже отсутствующей. Это
// подсказка, а не защита: доступ ограничивает BFF, что бы браузер ни нарисовал.
//
// КЛАВИША «/» НЕ ТРОГАЕТСЯ НАМЕРЕННО: она занята поисками экранов (GlobalSearch
// и поля разделов). Палитра живёт только на Cmd+K / Ctrl+K.
//
// G-ПОСЛЕДОВАТЕЛЬНОСТИ (g d — сделки, g k — компании…) заложены данными и видны
// в палитре как хоткеи сразу, но сам обработчик включается пропом
// `enableGSequences` и ПО УМОЛЧАНИЮ ВЫКЛЮЧЕН: одиночные клавиши поверх живых
// экранов включим отдельным шагом после обкатки, а не заодно.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { NAV_GROUPS, visibleItems } from './nav'
import './command-palette.css'

/** Где помним последние открытые из палитры разделы. */
const RECENT_KEY = 'cir-cpal-recent'
/** Сколько недавних показывать: больше пяти — уже не «недавние», а второе меню. */
const RECENT_MAX = 5
/** Сколько ждать вторую клавишу g-последовательности, мс (как в прототипе). */
const G_TIMEOUT_MS = 800

/**
 * Горячие клавиши переходов — данными, ключ — путь раздела из nav.ts.
 * Буквы — по первой букве раздела, где не занято; конфликтные получают соседнюю.
 * Показываются в палитре всегда (пассивное обучение), РАБОТАЮТ — только при
 * включённом enableGSequences.
 */
const HOTKEYS: Record<string, string[]> = {
  '/deals': ['g', 'd'],          // сделки
  '/companies': ['g', 'k'],      // компании
  '/contacts': ['g', 'c'],       // контакты
  '/vessels': ['g', 'v'],        // суда
  '/receivables': ['g', 'r'],    // дебиторка
  '/smart/invoices': ['g', 'i'], // счета клиенту
  '/stock': ['g', 's'],          // склад
  '/tasks': ['g', 't'],          // задачи
  '/dashboard': ['g', 'b'],      // кабинет руководителя (boss)
  '/analytics': ['g', 'a'],      // аналитика продаж
}

/** Команда палитры: что показать и что сделать. */
interface Command {
  /** Ключ отрисовки: путь для CRM, адрес для портала, с приставкой группы недавних. */
  id: string
  /** Заголовок группы в списке. */
  group: string
  label: string
  hint?: string
  keys?: string[]
  /** Внешняя ссылка на старый портал: открывается в новой вкладке, помечена ↗. */
  external: boolean
  run: () => void
}

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    if (Array.isArray(parsed)) return parsed.filter((x): x is string => typeof x === 'string')
  } catch {
    // Приватный режим Safari бросает на localStorage: палитра важнее памяти о недавнем.
  }
  return []
}

function pushRecent(to: string): string[] {
  const next = [to, ...loadRecent().filter((x) => x !== to)].slice(0, RECENT_MAX)
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch {
    // не смогли запомнить — не повод не перейти
  }
  return next
}

/**
 * Ранг совпадения: чем раньше вхождение в названии, тем выше. Название важнее
 * подсказки: совпадение только в hint уходит в самый низ с постоянной добавкой.
 * Нет вхождения нигде — команда отсеивается (null).
 */
function matchRank(c: Command, q: string): number | null {
  const inLabel = c.label.toLowerCase().indexOf(q)
  if (inLabel >= 0) return inLabel
  const inHint = (c.hint ?? '').toLowerCase().indexOf(q)
  if (inHint >= 0) return 1000 + inHint
  return null
}

/** Печатает ли человек в поле ввода: одиночные клавиши там принадлежат полю. */
const inField = (t: EventTarget | null): boolean => {
  const el = t as HTMLElement | null
  return !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable)
}

export function CommandPalette({ denied = () => false, enableGSequences = false }: {
  /** Предикат прав из useRightsApi().denied: закрытые разделы в команды не попадают. */
  denied?: (code?: string) => boolean
  /** Обработчик g-последовательностей. Выключен по умолчанию — включим после обкатки. */
  enableGSequences?: boolean
}) {
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [sel, setSel] = useState(0)
  // Версия «недавних»: перечитать localStorage после выполнения команды.
  const [recentTick, setRecentTick] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  // Куда вернуть фокус после закрытия: диалог не должен ронять фокус в пустоту.
  const prevFocus = useRef<HTMLElement | null>(null)

  const close = () => {
    setOpen(false)
    prevFocus.current?.focus?.()
  }

  /** Все доступные команды: NAV_GROUPS через тот же фильтр прав, что у меню. */
  const commands = useMemo<Command[]>(() => {
    const out: Command[] = []
    for (const g of NAV_GROUPS) {
      for (const i of visibleItems(g, denied)) {
        if (i.kind === 'crm') {
          out.push({
            id: i.to,
            group: g.title,
            label: i.label,
            hint: i.hint,
            keys: HOTKEYS[i.to],
            external: false,
            run: () => {
              pushRecent(i.to)
              setRecentTick((v) => v + 1)
              nav(i.to)
            },
          })
        } else {
          out.push({
            id: i.href,
            group: g.title,
            label: i.label,
            hint: `${i.portal}, откроется в новой вкладке${i.hint ? `. ${i.hint}` : ''}`,
            external: true,
            run: () => { window.open(i.href, '_blank', 'noopener') },
          })
        }
      }
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps -- denied меняется вместе с правами
  }, [denied, nav])

  /**
   * Что показываем: без запроса — «Недавние» и полный список группами; с запросом —
   * плоский список, отранжированный по месту вхождения (раньше — выше).
   */
  const view = useMemo<Command[]>(() => {
    const q = query.trim().toLowerCase()
    if (!q) {
      const recent = loadRecent()
        .map((to) => commands.find((c) => !c.external && c.id === to))
        .filter((c): c is Command => !!c)
        .map((c) => ({ ...c, id: `recent:${c.id}`, group: 'Недавние' }))
      return [...recent, ...commands]
    }
    return commands
      .map((c, i) => ({ c, i, rank: matchRank(c, q) }))
      .filter((x): x is { c: Command; i: number; rank: number } => x.rank !== null)
      .sort((a, b) => a.rank - b.rank || a.c.label.length - b.c.label.length || a.i - b.i)
      .map((x) => x.c)
    // recentTick — намеренная зависимость: команда выполнена → «Недавние» перечитаны.
  }, [commands, query, recentTick])

  // Индекс не должен указывать за конец после сужения списка запросом.
  const selIdx = Math.min(sel, Math.max(0, view.length - 1))

  const openPalette = () => {
    prevFocus.current = document.activeElement as HTMLElement | null
    setQuery('')
    setSel(0)
    setOpen(true)
  }

  // Фокус в поле — сразу после отрисовки диалога, без анимаций и ожиданий.
  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  // Выбранное видно всегда: список прокручивается к нему, а не наоборот.
  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]')
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' })
  }, [open, selIdx, view])

  // Cmd+K / Ctrl+K — глобально; g-последовательности — только при включённом пропе.
  useEffect(() => {
    let gPending = false
    let gTimer: ReturnType<typeof setTimeout> | undefined

    const onKey = (e: KeyboardEvent) => {
      // e.code, а не e.key: на русской раскладке та же клавиша даёт «л».
      const isK = e.code === 'KeyK' || e.key.toLowerCase() === 'k' || e.key.toLowerCase() === 'л'
      if ((e.metaKey || e.ctrlKey) && !e.altKey && isK) {
        e.preventDefault()
        if (open) close(); else openPalette()
        return
      }
      if (!enableGSequences || open) return
      if (e.metaKey || e.ctrlKey || e.altKey || inField(e.target)) return
      if (gPending) {
        gPending = false
        clearTimeout(gTimer)
        const hit = Object.entries(HOTKEYS)
          .find(([, keys]) => keys[1] === e.key.toLowerCase())
        // Переход только в доступный раздел: hotkey закрытого правами не работает.
        if (hit && commands.some((c) => !c.external && c.id === hit[0])) {
          e.preventDefault()
          pushRecent(hit[0])
          setRecentTick((v) => v + 1)
          nav(hit[0])
        }
        return
      }
      if (e.key === 'g') {
        gPending = true
        gTimer = setTimeout(() => { gPending = false }, G_TIMEOUT_MS)
      }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      clearTimeout(gTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- close/openPalette стабильны по смыслу
  }, [open, enableGSequences, commands, nav])

  if (!open) return null

  const runCommand = (c: Command) => {
    close()
    c.run()
  }

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSel(Math.min(view.length - 1, selIdx + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSel(Math.max(0, selIdx - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const c = view[selIdx]
      if (c) runCommand(c)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      close()
    } else if (e.key === 'Tab') {
      // Ловушка фокуса: единственное поле диалога — ввод, Tab из него не уводит.
      e.preventDefault()
    }
  }

  const searching = query.trim() !== ''

  return (
    <>
      {/* Клик мимо палитры закрывает её; Escape делает то же с клавиатуры. */}
      <div className="cpal-veil" aria-hidden="true" onClick={close} />
      <div className="cpal" role="dialog" aria-modal="true" aria-label="Командная палитра">
        <input
          ref={inputRef}
          className="cpal-input"
          type="text"
          placeholder="Команда или переход…"
          aria-label="Поиск команды"
          role="combobox"
          aria-expanded="true"
          aria-controls="cpal-list"
          aria-activedescendant={view[selIdx] ? `cpal-opt-${selIdx}` : undefined}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSel(0) }}
          onKeyDown={onInputKey}
        />
        <div className="cpal-list" id="cpal-list" role="listbox" ref={listRef}>
          {view.length === 0 && <div className="cpal-empty">Ничего не найдено</div>}
          {view.map((c, i) => (
            <div key={c.id + String(i)} style={{ display: 'contents' }}>
              {/* Заголовки групп — только у полного списка: при поиске порядок
                  задаёт ранг совпадения, и группы бы перемешались. */}
              {!searching && (i === 0 || view[i - 1].group !== c.group) && (
                <div className="cpal-group">{c.group}</div>
              )}
              <div
                className="cpal-item"
                id={`cpal-opt-${i}`}
                role="option"
                aria-selected={i === selIdx}
                title={c.hint}
                onMouseMove={() => setSel(i)}
                onClick={() => runCommand(c)}
              >
                <span className="cpal-label">
                  {c.label}
                  {c.external && <span className="cpal-ext" aria-hidden="true"> ↗</span>}
                </span>
                {/* При поиске группа подписана в строке: заголовков-то нет. */}
                {searching && <span className="cpal-where">{c.group}</span>}
                <span className="cpal-spacer" />
                {c.keys?.map((k) => <kbd className="cpal-kbd" key={k}>{k}</kbd>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
